/**
 * Phase 1E.2 — Tenant sidebar nav structure.
 *
 * The dynamic tenant sidebar reuses the same NavGroup/NavItem render
 * components as the root sidebar (in Layout.js) — it just feeds them
 * a different `groups` array. This module owns the construction of
 * that array.
 *
 * ## Shape mirrors NAV_GROUPS
 *
 * Each group has { id, label, icon?, collapsible, defaultOpen?, items }
 * and each item has { path, label, icon }. App and spine items carry
 * NO permission gate — tenant users by definition have access to what
 * is granted to their tenant, and fine-grained differences inside a
 * tenant are enforced at the route layer, not by hiding sidebar items.
 *
 * ## The administration group is the one exception (2G, 2G.2)
 *
 * `roles.manage` and `users.view` are held by `tenant_admin` and not by
 * `tenant_member`, so both entries in that group are gated. Hiding rather than showing-and-403ing is
 * right here because it is not an app the tenant might buy: it is a
 * capability half their users structurally do not have, and a nav item
 * that always fails is worse than an absent one.
 *
 * The gate is declared on the item and applied by the shared filter in
 * Layout.js (`filteredGroups`), which already reads `item.permission`
 * and drops a group whose items all filter out. No Layout change was
 * needed to add this — the pipeline was built for it.
 *
 * ## Slug → route/icon map (APP_SLUG_NAV)
 *
 * The backend returns granted apps with `slug` (e.g. "projects",
 * "finance"). The SPA has its own routing table; we map slug to the
 * existing route and a lucide icon component here. Apps with no
 * mapping are skipped from the sidebar with a console warning — the
 * deployment ordering for new apps is:
 *
 *   1. Build the app + register its route in the SPA
 *   2. Add an entry to APP_SLUG_NAV below
 *   3. Ingest the app via the App Ingestion module
 *   4. Grant it to whichever tenants should see it (1E.1 admin Apps tab)
 *
 * Skipping the map entry between steps 1 and 2 just means the app
 * doesn't appear in tenant sidebars yet — no broken links, no crashes.
 */
import {
  Sparkles,
  Bot,
  Package,
  Landmark,
  FolderKanban,
  GitMerge,
  Inbox,
  Shield,
  Settings,
  Building2,
  Users,
  ScrollText,
} from 'lucide-react';

/**
 * Map from app_registry.slug to the SPA's nav configuration for that
 * app. Add new entries here as new apps come online. The "icon" is a
 * lucide component (imported above); "route" is the SPA path the
 * sidebar item links to. Both fields required.
 */
export const APP_SLUG_NAV = {
  finance: { route: '/finance', icon: Landmark },
  projects: { route: '/projects', icon: FolderKanban },
  // MATCH — AppDirect order-matching engine. Route /match/* mounted in
  // App.js (gated by PermissionRoute on `match.access`). Added 22 May
  // 2026 alongside Phase 1E.3c-match.registry, which seeded the
  // ``app_registry`` row with ``status="active"`` and populated the
  // 14 ``match.*`` permission keys for the 1E.3d auto-seeder.
  match: { route: '/match', icon: GitMerge },
  // AI Dispatcher — AppDirect inbound-mailbox email classifier. Route
  // /ai-dispatcher/* mounted in App.js (gated by PermissionRoute on
  // `ai_dispatcher.teams.view`). Added 25 May 2026 alongside Phase
  // 1E.3c-ai-dispatcher, which made the module true-multi-tenant
  // (per-tenant settings singleton + per-tenant n8n_token + tenant-
  // scoped teams + decisions). The 1E.3d auto-seeder copies all 11
  // ``ai_dispatcher.*`` permission keys onto tenant_admin role on grant.
  ai_dispatcher: { route: '/ai-dispatcher', icon: Inbox },
  // Register additional app slugs here as their routes get scoped
  // by tenant_id in 1E.3+. Each new entry needs a corresponding
  // route already mounted in the SPA router.
};

// ─── Route → slug, the inverse of APP_SLUG_NAV (2I.1b) ──────────────
//
// Sorted longest-route-first so a nested app route ('/finance/reports')
// would win over a shorter one that prefixes it, whichever order the
// object literal above happens to be written in.
//
// Lowercased on both sides because React Router v6 matches paths
// case-insensitively by default. Comparing case-sensitively here would
// mean '/AI-Dispatcher' rendered the page while the gate found no slug
// for it — a bypass reachable by holding shift.
const APP_ROUTE_INDEX = Object.entries(APP_SLUG_NAV)
  .map(([slug, cfg]) => [
    String(cfg?.route || '').toLowerCase().replace(/\/+$/, ''),
    slug,
  ])
  .filter(([route]) => route.startsWith('/'))
  .sort((a, b) => b[0].length - a[0].length);

/**
 * The app slug that owns a given SPA path, or null if the path is not
 * inside a registered app's route.
 *
 * ## Why this exists
 *
 * `PermissionRoute` supports an `appSlug` prop that refuses a route when
 * the app is not in `user.granted_app_slugs`. It works, and until 2I.1b
 * `App.js` declared it on two Finance routes out of every app route in
 * the SPA — so `/match` and `/ai-dispatcher` were gated on permissions
 * alone, and a tenant admin holds those permissions via the 1E.3d
 * auto-seeder. The check passed and nothing ever consulted the grant.
 *
 * That is the same shape as `get_tenant_context`: a check most callers
 * must remember to opt into is a check most callers will not opt into,
 * and it went unnoticed for the same reason — an opt-in gate nobody
 * opted into looks identical from the outside to no gate at all.
 *
 * Deriving from APP_SLUG_NAV rather than from a new list because that
 * map already carries the route, and adding an entry to it is already
 * step 2 of the four-step ordering for bringing a new app online (see
 * the module docstring). An app that reaches step 4 — granted to a
 * tenant — cannot have skipped step 2, so it cannot reach a user
 * ungated.
 *
 * @param {string} pathname — `useLocation().pathname`.
 * @returns {string|null} the app slug, or null for non-app routes.
 */
export function appSlugForPath(pathname) {
  if (typeof pathname !== 'string' || !pathname) return null;
  const path = pathname
    .toLowerCase()
    .split('?')[0]
    .split('#')[0]
    .replace(/\/+$/, '');
  if (!path.startsWith('/')) return null;
  for (const [route, slug] of APP_ROUTE_INDEX) {
    // Equality or a segment boundary — never a bare prefix, or '/match'
    // would claim '/matching'.
    if (path === route || path.startsWith(`${route}/`)) return slug;
  }
  return null;
}

/**
 * Builds the NAV_GROUPS-shaped array for a non-root tenant user.
 *
 * @param {Array<{ slug: string, name: string }>} grantedApps —
 *   the `apps` field from /tenant/sidebar-config.
 * @returns NAV_GROUPS-shaped array consumable by SidebarContent.
 */
export function buildTenantNavGroups(grantedApps) {
  const apps = (grantedApps || [])
    .map((app) => {
      const cfg = APP_SLUG_NAV[app.slug];
      if (!cfg) {
        if (typeof console !== 'undefined') {
          console.warn(
            `[TenantSidebar] No frontend nav config for app slug='${app.slug}'. ` +
              'Skipping. Add it to APP_SLUG_NAV in lib/tenantAppNav.js.',
          );
        }
        return null;
      }
      return {
        path: cfg.route,
        label: app.name,
        icon: cfg.icon,
      };
    })
    .filter(Boolean);

  // Spine — always present. AI Employees and AI Chat are universally
  // available to tenant users. Permission gates intentionally omitted
  // (see module docstring).
  const groups = [
    {
      id: 'tenant-core',
      label: null, // ungrouped, no header
      collapsible: false,
      items: [
        { path: '/ai-employees', label: 'AI Employees', icon: Sparkles },
        { path: '/chat', label: 'AI Chat', icon: Bot },
      ],
    },
  ];

  // Apps group — only renders when at least one app is granted. An
  // empty Apps section would be confusing for users on a freshly
  // provisioned tenant; better to show nothing than an empty header.
  if (apps.length > 0) {
    groups.push({
      id: 'tenant-apps',
      label: 'Apps',
      icon: Package,
      collapsible: false,
      items: apps,
    });
  }

  // Administration — 2G. Phase 2D gave tenant admins their own role
  // copies to clone and edit, and 2C added the tenant-visible roles
  // filter behind `GET /api/roles`. Neither was reachable: Layout.js
  // hands non-root tenants THIS array, and until now it contained only
  // the spine and granted apps, so the page existed with no door to it.
  //
  // `/admin/roles` is the same route and the same `RolesPermissions`
  // component the root sidebar uses. Reused rather than duplicated
  // because the backend already returns the right thing per caller:
  // `list_roles` scopes through `tenant_visible_roles_filter(tenant_id)`,
  // so a tenant admin sees the global platform roles plus their own
  // tenant's copies, and nobody else's. The `/admin` path prefix is
  // cosmetically odd for a tenant, but changing it would mean a second
  // route mounting the same component, and two paths to one screen is a
  // worse problem than an ugly URL.
  //
  // Gated on `roles.manage`, matching the `PermissionRoute` on the route
  // itself in App.js. `roles.view` would be the wrong gate: it grants
  // read of the roles list, but the page's whole purpose is authoring,
  // and every button on it needs manage.
  //
  // Not added to `roleHome.js`: a tenant admin should land on their
  // granted app or the AI Employees spine, not on an administration
  // tool. Roles is somewhere you go, not somewhere you start.
  // 2G.2 adds Users beside it. The same six keys landed on every
  // `tenant_admin` copy in the 21 Aug baseline sync — `roles.view`,
  // `roles.manage`, `users.view`, `users.invite`, `users.edit`,
  // `users.assign_role` — and 2G only gave the first two a home.
  //
  // `/admin/tenant-users`, NOT `/admin/users`. The latter is the root User
  // & Resource Management screen: contractors, resource types,
  // assignability flags, contractor rate approval. Reusing one component
  // for both audiences worked for Roles because the backend already
  // returned the right thing per caller; it does not work here, so 2G.2
  // adds a separate tenant page over the same `TenantUserList`.
  //
  // Gated on `users.view`, matching the PermissionRoute in App.js. Unlike
  // Roles — where `roles.view` would have been the wrong gate because the
  // page exists to author — this page is genuinely useful read-only, and
  // `users.view` is already the line between `tenant_admin` (holds it) and
  // `tenant_member` (does not). The Invite button inside the page carries
  // its own `users.invite` check.
  // 1C adds Audit Log as the third. Same shape as the two above and the
  // same reason: `routes/audit.py` was already tenant-scoped, the screen
  // already existed, `App.js` already gated the route on `audit.view` —
  // and 1B put that key on every `tenant_admin`. Without an entry here
  // the only way in is typing the URL, which is not a feature.
  //
  // `/admin/audit`, reusing the root sidebar's path and the same
  // `AuditLog` component, on the Roles precedent rather than the Users
  // one: the backend already returns the right thing per caller. 1A
  // scoped the row query, the module dropdown and the event counts to
  // the caller's tenant, so a tenant admin on this route sees their own
  // activity and nobody else's. A separate tenant page would be a second
  // route onto one component with no difference in what it renders.
  //
  // Gated on `audit.view`, matching the PermissionRoute in App.js and
  // the root sidebar entry in Layout.js. Deliberately NOT
  // `chat_logs.view`: 1B split those, and this group is the door to the
  // activity history. The transcripts tab gates itself inside the page,
  // so an administrator holding only `audit.view` gets the entry, the
  // page, and one tab.
  //
  // Not added to `roleHome.js`, for the same reason as Roles — an
  // administration tool is somewhere you go, not somewhere you land.
  groups.push({
    id: 'tenant-admin',
    label: 'Administration',
    icon: Settings,
    collapsible: false,
    items: [
      {
        path: '/admin/tenant-users',
        label: 'Users',
        icon: Users,
        permission: 'users.view',
      },
      {
        path: '/admin/roles',
        label: 'Roles & Permissions',
        icon: Shield,
        permission: 'roles.manage',
      },
      {
        path: '/admin/audit',
        label: 'Audit Log',
        icon: ScrollText,
        permission: 'audit.view',
      },
      // Roadmap item 9. Ryan, 9 Sep 2026: make branding available in EVERY
      // tenant, so a tenant admin sets their own logo and name.
      //
      // The entry has to be HERE, not only in Layout.js's NAV_GROUPS. That
      // list is the root sidebar; tenant users are served by this file, so
      // granting branding.manage to tenant_admin gave them the permission
      // and the route while leaving no way to reach it. Same shape as Roles
      // and Audit Log above: one route, two navs.
      {
        path: '/admin/branding',
        label: 'Organization Branding',
        icon: Building2,
        permission: 'branding.manage',
      },
    ],
  });

  return groups;
}
